import React, {useState} from 'react';

import './CreateAccount.scss';
import CreateAccountUser, {InterfaceCreateAccountUser} from './CreateAccountUser/CreateAccountUser';
import CreateAccountEnterprise, {
    InterfaceCreateAccountEnterprise
} from "./CreateAccountEnterprise/CreateAccountEnterprise";
import CreateAccountMailing, {InterfaceCreateAccountMailing} from "./CreateAccountMailing/CreateAccountMailing";
import {AddUser} from "../../service/AdminService";
import {useNavigate} from "react-router-dom";
import FCAlertMsg, {TypeAlertMsg} from "../../components/FCObjets/FCAlertMsg/FCAlertMsg";
import FCSpinner from "../../components/FCObjets/FCSpinner";

type Step = {
    key: string;
    label: string;
    icon: string;
};

const STEPS: Step[] = [
    {key: "identity", label: "Identity", icon: "person"},
    {key: "business", label: "Business", icon: "business_center"},
    {key: "mailing", label: "SMTP Settings", icon: "mail"},
];

function CreateAccount() {

    const [indexComponent, setIndexComponent] = useState(0);
    const [dataMailing, setDataMailing] = useState<InterfaceCreateAccountMailing>();
    const [dataUser, setDataUser] = useState<InterfaceCreateAccountUser>();
    const [dataEnterprise, setDataEnterprise] = useState<InterfaceCreateAccountEnterprise>();

    const [verifyInput, setVerifyInput] = useState(false);
    const [errorInput, setErrorInput] = useState<string | undefined>(undefined);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const navigate = useNavigate();

    const submit = () => {
        setVerifyInput(true);
        setErrorInput(undefined);

        if (!dataUser || !dataEnterprise || !dataMailing) {
            const missing: string[] = [];
            if (!dataUser) missing.push("Identité");
            if (!dataEnterprise) missing.push("Entreprise");
            if (!dataMailing) missing.push("SMTP");
            setErrorInput(`Veuillez compléter l'onglet : ${missing.join(", ")}.`);
            return;
        }

        const errors: string[] = [];

        // Identité
        if (!dataUser.civility) errors.push("La civilité est obligatoire.");
        if (!dataUser.firstname) errors.push("Le prénom est obligatoire.");
        if (!dataUser.lastname) errors.push("Le nom est obligatoire.");
        if (!dataUser.username) errors.push("Le nom d'utilisateur est obligatoire ou déjà pris.");
        if (!dataUser.contactEmail) errors.push("L'adresse email est obligatoire.");
        if (!dataUser.password) errors.push("Le mot de passe est obligatoire.");

        // Entreprise
        if (!dataEnterprise.companyName) errors.push("Le nom de l'entreprise est obligatoire.");
        if (!dataEnterprise.companySiret) errors.push("Le SIRET est obligatoire.");
        if (!dataEnterprise.companyLocation) errors.push("L'adresse de l'entreprise est obligatoire.");
        if (!dataEnterprise.declarationLocation) errors.push("La ville de déclaration est obligatoire.");

        if (errors.length > 0) {
            setErrorInput(errors.join(" "));
            return;
        }

        if (dataUser.password.length < 8) {
            setErrorInput("Le mot de passe doit contenir au moins 8 caractères.");
            return;
        }

        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(dataUser.contactEmail)) {
            setErrorInput("L'adresse email n'est pas valide.");
            return;
        }

        if (dataEnterprise.companySiret.replace(/\s/g, "").length !== 14 || !/^\d{14}$/.test(dataEnterprise.companySiret.replace(/\s/g, ""))) {
            setErrorInput("Le numéro SIRET doit contenir exactement 14 chiffres.");
            return;
        }

        const user: any = {
            user: {
                firstname: dataUser.firstname,
                lastname: dataUser.lastname,
                username: dataUser.username,
                password: dataUser.password,
                civility: dataUser.civility,
                contactEmail: dataUser.contactEmail,
                phoneNumber: dataUser.phoneNumber,
                userInfo: {
                    userSMTP: dataMailing.userSmtp,
                    nameServerSMTP: dataMailing.serverSMTP,
                    passSMTP: dataMailing.passSMTP,
                    tjmDefault: dataEnterprise.tjm,
                    companyName: dataEnterprise.companyName,
                    companySiret: dataEnterprise.companySiret,
                    companyLocation: dataEnterprise.companyLocation,
                    declarationLocation: dataEnterprise.declarationLocation,
                    titleJob: dataUser.titleJob,
                    urlCV: dataUser.urlCV,
                    tvaNumber: dataEnterprise.tvaNumber,
                }
            }
        };
        setIsSubmitting(true);
        AddUser(user).then(() => navigate('wait_validate')).catch((e) => {
            setIsSubmitting(false);
            const msg = e?.response?.data?.message || "Une erreur est survenue lors de la création du compte.";
            setErrorInput(msg);
        });
    };

    const goTo = (nextComponent: boolean) => {
        setErrorInput("");
        if (nextComponent && indexComponent + 1 <= STEPS.length - 1) {
            setIndexComponent(indexComponent + 1);
        }
        if (!nextComponent && indexComponent - 1 >= 0) {
            setIndexComponent(indexComponent - 1);
        }
    };

    return (
        <div className="caPage">
            {/* Blocage mobile */}
            <div className="caMobileBlock">
                <img src={`${process.env.PUBLIC_URL}/logo_fc.svg`} alt="freeCompta" className="caMobileBlockLogo"/>
                <h1 className="caMobileBlockTitle">Free Compta</h1>
                <p className="caMobileBlockDesc">
                    La création d'un compte n'est disponible que sur la version ordinateur.
                    Merci de vous reconnecter depuis un écran plus large pour finaliser votre inscription.
                </p>
                <button className="caMobileBlockBtn" onClick={() => navigate('/')}>
                    <span className="material-symbols-outlined">arrow_back</span>
                    Retour à la connexion
                </button>
            </div>

            {/* Onboarding desktop */}
            <div className="caDesktop">
                {/* Sidebar */}
                <aside className="caSidebar">
                    <div className="caSidebarBrand">
                        <img src={`${process.env.PUBLIC_URL}/logo_fc.svg`} alt="FreeCompta" className="caSidebarLogo"/>
                        <span className="caSidebarBrandText">FreeCompta</span>
                    </div>
                    <div className="caSidebarTitleBlock">
                        <div className="caSidebarTitle">Onboarding</div>
                        <div className="caSidebarSubtitle">Configurer votre compte</div>
                    </div>
                    <nav className="caSidebarNav">
                        {STEPS.map((step, i) => (
                            <button
                                key={step.key}
                                type="button"
                                className={`caSidebarNavItem${indexComponent === i ? " active" : ""}`}
                                onClick={() => setIndexComponent(i)}
                            >
                                <span className="material-symbols-outlined">{step.icon}</span>
                                <span className="caSidebarNavLabel">{step.label}</span>
                            </button>
                        ))}
                    </nav>
                </aside>

                {/* Main */}
                <main className="caMain">
                    <div className="caMainInner">
                        <div style={{display: indexComponent === 0 ? "block" : "none"}}>
                            <CreateAccountUser verifyInput={verifyInput}
                                               handlerChange={(dataUser) => setDataUser(dataUser)}/>
                        </div>
                        <div style={{display: indexComponent === 1 ? "block" : "none"}}>
                            <CreateAccountEnterprise verifyInput={verifyInput}
                                                     handlerChange={(dataEnterprise) => setDataEnterprise(dataEnterprise)}/>
                        </div>
                        <div style={{display: indexComponent === 2 ? "block" : "none"}}>
                            <CreateAccountMailing handlerChange={(dataMailing) => setDataMailing(dataMailing)}/>
                        </div>

                        {errorInput && (
                            <div className="caError">
                                <FCAlertMsg textMsg={errorInput} type={TypeAlertMsg.ERROR} activate={!!errorInput}/>
                            </div>
                        )}

                        <div className="caActions">
                            {indexComponent !== 0 ? (
                                <button type="button" className="caBtnTertiary" onClick={() => goTo(false)}>
                                    <span className="material-symbols-outlined">arrow_back</span>
                                    Retour
                                </button>
                            ) : <span/>}

                            {indexComponent < STEPS.length - 1 && (
                                <button type="button" className="caBtnPrimary" onClick={() => goTo(true)}>
                                    Continuer
                                    <span className="material-symbols-outlined">arrow_forward</span>
                                </button>
                            )}
                            {indexComponent === STEPS.length - 1 && (
                                <button type="button" className="caBtnPrimary" onClick={submit} disabled={isSubmitting}>
                                    {isSubmitting ? (
                                        <>
                                            <FCSpinner size="small"/>
                                            Création en cours…
                                        </>
                                    ) : (
                                        <>
                                            Terminé
                                            <span className="material-symbols-outlined">check</span>
                                        </>
                                    )}
                                </button>
                            )}
                        </div>
                    </div>
                </main>
            </div>
        </div>
    );
}

export default CreateAccount;
